/**
 * Dashboard collector edit form — inline editor for a single collector,
 * including kringen editing.
 *
 * Replicated from `CollectorEditForm` in CollectorenTab.tsx (Igor accepted the
 * duplication; CollectorenTab stays untouched). Save orchestration mirrors the
 * original: collector fields via `updateCollector` (only writing `systeem` when
 * it actually changed), kringen via create/update/delete diffing against the
 * originally-loaded set, then `fetchCollectoren` to refresh the store.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Collector, Kring } from '@/types/odoo';
import type { CollectorPicking } from '@/api/tasks';
import { updateCollector, fetchKringen, createKring, updateKring, deleteKring, deleteCollector } from '@/api/tasks';
import { useProjectViewStore } from '@/stores/projectView';
import { DashboardKringenEditor, type LocalKring } from './DashboardKringenEditor';
import { DashboardCollectorFields } from './DashboardCollectorFields';
import { CollectorSplitModal } from './collectoren/CollectorSplitModal';

export function DashboardCollectorEditForm({
  collector,
  projectId,
  existingBloks,
  existingVerdiepingen,
  pickings,
  installerNames,
  onClose,
  onChanged,
}: {
  collector: Collector;
  projectId: number;
  existingBloks: string[];
  existingVerdiepingen: string[];
  pickings: CollectorPicking[];
  /** Odoo partner id → name, for the optional installateur on a split's deel 2. */
  installerNames?: Record<string, string>;
  onClose: () => void;
  /** Called after a successful save/delete so the parent can refresh the fase mapping. */
  onChanged?: () => void;
}) {
  const [name, setName] = useState(collector.name);
  const [verdieping, setVerdieping] = useState(collector.x_studio_verdieping || '');
  const [blok, setBlok] = useState(collector.x_studio_blok ? String(collector.x_studio_blok[1] || '') : '');
  const [m2, setM2] = useState(String(collector.x_studio_collector_m2 || ''));
  const initialSysteem = collector.x_studio_collector_systeem || 'tacker';
  const [systeem, setSysteem] = useState(initialSysteem);
  const [materiaallijstId, setMateriaallijstId] = useState<number | null>(collector.materiaallijst_id ?? null);
  // Legpatroon at collector level — derive initial value from kringen.
  const [legpatroon, setLegpatroon] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showSplit, setShowSplit] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const fetchCollectoren = useProjectViewStore((s) => s.fetchCollectoren);

  // Local kringen state — buffered, only saved on "Opslaan".
  const [localKringen, setLocalKringen] = useState<LocalKring[]>([]);
  const [deletedKringIds, setDeletedKringIds] = useState<Set<number>>(new Set());
  const [kringenLoading, setKringenLoading] = useState(true);
  const [kringenError, setKringenError] = useState<string | null>(null);
  const [kringenDirty, setKringenDirty] = useState(false);
  const nextTempIdRef = useRef(-1);
  const originalKringenRef = useRef<LocalKring[]>([]);

  // Fetch kringen on mount, store as original + local copy.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setKringenLoading(true);
      setKringenError(null);
      try {
        const res = await fetchKringen(collector.id);
        if (cancelled) return;
        const mapped: LocalKring[] = res.kringen.map((k: Kring) => ({
          id: k.id,
          x_kringnummer: k.x_kringnummer,
          x_system: k.x_system || '',
          x_legpatroon: k.x_legpatroon,
          x_length: k.x_length,
        }));
        originalKringenRef.current = mapped;
        setLocalKringen(mapped);
        // Derive collector-level legpatroon from kringen.
        if (mapped.length > 0) {
          const values = mapped.map((k) => k.x_legpatroon);
          const allSame = values.every((v) => v === values[0]);
          if (allSame && values[0]) {
            const v = String(values[0]);
            setLegpatroon(['10', '15', '20'].includes(v) ? v : `custom:${v}`);
          } else if (!allSame) {
            setLegpatroon('meerdere');
          }
        }
      } catch (err) {
        if (!cancelled) setKringenError(err instanceof Error ? err.message : 'Kringen laden mislukt');
      } finally {
        if (!cancelled) setKringenLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [collector.id]);

  const handleKringUpdate = useCallback((id: number, field: string, value: string) => {
    setKringenDirty(true);
    setLocalKringen((prev) =>
      prev.map((k) => {
        if (k.id !== id) return k;
        if (field === 'x_kringnummer' || field === 'x_legpatroon' || field === 'x_length') {
          // Accept a Belgian comma decimal (7,5 → 7.5) so it doesn't zero out silently.
          const numVal = Number(value.replace(',', '.'));
          return { ...k, [field]: isNaN(numVal) ? 0 : numVal };
        }
        return { ...k, [field]: value };
      }),
    );
  }, []);

  const handleKringAdd = useCallback(() => {
    setKringenDirty(true);
    const nextNum = localKringen.length > 0 ? Math.max(...localKringen.map((k) => k.x_kringnummer)) + 1 : 1;
    const tempId = nextTempIdRef.current;
    nextTempIdRef.current -= 1;
    // In mixed state ("meerdere systemen") inherit from the first kring — never store the label as a kring system.
    const fallbackSysteem = systeem === 'meerdere systemen' ? localKringen[0]?.x_system || 'tacker' : systeem;
    setLocalKringen((prev) => [...prev, { id: tempId, x_kringnummer: nextNum, x_system: fallbackSysteem, x_legpatroon: 0, x_length: 0 }]);
  }, [localKringen, systeem]);

  const handleKringDelete = useCallback((id: number) => {
    setKringenDirty(true);
    setLocalKringen((prev) => prev.filter((k) => k.id !== id));
    // Track deletion of existing kringen (positive IDs) for the API call on save.
    if (id > 0) {
      setDeletedKringIds((prev) => new Set(prev).add(id));
    }
  }, []);

  // Derive collector-level systeem from kringen: all same → that value, mixed → "meerdere systemen".
  const derivedSysteem = useMemo(() => {
    const values = localKringen.map((k) => k.x_system).filter(Boolean);
    if (values.length === 0) return systeem;
    const unique = new Set(values);
    if (unique.size === 1) return values[0];
    return 'meerdere systemen';
  }, [localKringen, systeem]);

  // Sync collector dropdown with derived value, but only after the user actually
  // edits kringen — avoids silently rewriting stored state on no-op opens.
  useEffect(() => {
    if (kringenDirty && derivedSysteem !== systeem) setSysteem(derivedSysteem);
  }, [derivedSysteem, kringenDirty, systeem]);

  const handleDelete = async () => {
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteCollector(collector.id);
      fetchCollectoren(projectId);
      onChanged?.(); // refresh the parent's fase mapping
      onClose();
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Verwijderen mislukt');
    } finally {
      setDeleting(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      // 1. Save collector fields. Only write systeem if it actually changed — otherwise
      //    we silently rewrite stored "meerdere systemen" to a kring value on no-op edits.
      const collectorPayload: Parameters<typeof updateCollector>[1] = {
        name: name.trim(),
        verdieping: verdieping.trim(),
        blok: blok.trim(),
        collector_m2: parseFloat(m2) || 0,
        materiaallijst_id: materiaallijstId,
      };
      if (derivedSysteem !== initialSysteem) {
        collectorPayload.systeem = derivedSysteem;
      }
      await updateCollector(collector.id, collectorPayload);

      // 2. Delete removed kringen (only existing ones with positive IDs).
      for (const kringId of deletedKringIds) {
        await deleteKring(collector.id, kringId);
      }

      // 3. Create new kringen (negative IDs).
      for (const k of localKringen.filter((k) => k.id < 0)) {
        await createKring(collector.id, { x_kringnummer: k.x_kringnummer, x_system: k.x_system, x_legpatroon: k.x_legpatroon, x_length: k.x_length });
      }

      // 4. Update changed existing kringen (positive IDs, not deleted).
      for (const k of localKringen.filter((k) => k.id > 0)) {
        const orig = originalKringenRef.current.find((o) => o.id === k.id);
        if (!orig) continue;
        const changes: Record<string, number | string> = {};
        if (k.x_kringnummer !== orig.x_kringnummer) changes.x_kringnummer = k.x_kringnummer;
        if (k.x_system !== orig.x_system) changes.x_system = k.x_system;
        if (k.x_legpatroon !== orig.x_legpatroon) changes.x_legpatroon = k.x_legpatroon;
        if (k.x_length !== orig.x_length) changes.x_length = k.x_length;
        if (Object.keys(changes).length > 0) {
          await updateKring(collector.id, k.id, changes);
        }
      }

      fetchCollectoren(projectId);
      onChanged?.(); // refresh the parent's fase mapping (blok/verdieping/materiaallijst may change grouping)
      onClose();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Opslaan mislukt');
    } finally {
      setSaving(false);
    }
  };

  // Systeem change: propagate to kringen, except for the "meerdere systemen"
  // derived label — that leaves kring values alone.
  const handleSysteemChange = (newSysteem: string) => {
    setSysteem(newSysteem);
    setKringenDirty(true);
    if (newSysteem !== 'meerdere systemen') {
      setLocalKringen((prev) => prev.map((k) => ({ ...k, x_system: newSysteem })));
    }
  };

  // Legpatroon picker: 10/15/20 set a fixed pattern, 'custom' prompts, 'meerdere'
  // is a no-write label. Numeric choices propagate to every kring.
  const handleLegpatroonChange = (choice: string) => {
    if (choice === 'meerdere') {
      setLegpatroon('meerdere');
      setKringenDirty(true);
      return;
    }
    if (choice === 'custom') {
      const raw = prompt('Legpatroon (getal):');
      const value = raw?.replace(',', '.'); // accept a Belgian comma decimal (7,5 → 7.5)
      if (value && !isNaN(Number(value)) && Number(value) > 0) {
        setLegpatroon(`custom:${value}`);
        setKringenDirty(true);
        setLocalKringen((prev) => prev.map((k) => ({ ...k, x_legpatroon: Number(value) })));
      }
      return;
    }
    setLegpatroon(choice);
    setKringenDirty(true);
    setLocalKringen((prev) => prev.map((k) => ({ ...k, x_legpatroon: Number(choice) })));
  };

  return (
    <div className="bg-white rounded-xl border border-td-blue/20 shadow-md p-5" onClick={(e) => e.stopPropagation()}>
      <h3 className="text-sm font-bold text-td-navy mb-4">Collector Bewerken</h3>

      <DashboardCollectorFields
        collectorId={collector.id}
        name={name}
        onName={setName}
        verdieping={verdieping}
        onVerdieping={setVerdieping}
        blok={blok}
        onBlok={setBlok}
        m2={m2}
        onM2={setM2}
        systeem={systeem}
        onSysteem={handleSysteemChange}
        legpatroon={legpatroon}
        onLegpatroon={handleLegpatroonChange}
        materiaallijstId={materiaallijstId}
        onMateriaallijst={setMateriaallijstId}
        existingBloks={existingBloks}
        existingVerdiepingen={existingVerdiepingen}
        pickings={pickings}
      />

      {saveError && (
        <div className="mt-3 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{saveError}</div>
      )}

      <div className="mt-4 border-t border-gray-100 pt-4">
        {kringenLoading ? (
          <div className="py-2 flex items-center gap-2 text-xs text-td-muted">
            <div className="w-4 h-4 border-2 border-td-blue/30 border-t-td-blue rounded-full animate-spin" role="status">
              <span className="sr-only">Kringen laden...</span>
            </div>
            Kringen laden...
          </div>
        ) : kringenError ? (
          <p className="text-xs text-red-500 mb-2">{kringenError}</p>
        ) : (
          <DashboardKringenEditor
            kringen={localKringen}
            onUpdate={handleKringUpdate}
            onAdd={handleKringAdd}
            onDelete={handleKringDelete}
          />
        )}
      </div>

      <div className="mt-4 flex items-center gap-3">
        <button type="button" onClick={handleSave} disabled={saving}
          className="px-4 py-2 text-sm font-semibold bg-td-blue text-white rounded-lg hover:bg-td-blue/90 transition disabled:opacity-50">
          {saving ? 'Opslaan...' : 'Opslaan'}
        </button>
        <button type="button" onClick={onClose}
          className="px-4 py-2 text-sm font-semibold text-td-muted hover:text-td-navy transition">
          Annuleren
        </button>
        <button
          type="button"
          onClick={() => setShowSplit(true)}
          className="ml-auto px-3 py-1.5 text-sm font-semibold text-td-blue border border-td-blue/40 rounded-lg hover:bg-td-blue/5 transition"
        >
          Splitsen
        </button>
        <button
          type="button"
          onClick={() => setShowDeleteConfirm(true)}
          className="px-3 py-1.5 text-sm font-semibold text-red-600 border border-red-300 rounded-lg hover:bg-red-50 transition"
        >
          Verwijder
        </button>
      </div>

      {showSplit && (
        <CollectorSplitModal
          collector={collector}
          installerNames={installerNames}
          onClose={() => setShowSplit(false)}
          onSplit={() => {
            fetchCollectoren(projectId);
            onChanged?.();
            onClose();
          }}
        />
      )}

      {showDeleteConfirm && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-800 font-semibold mb-2">
            Weet je zeker dat je &quot;{collector.name}&quot; wilt verwijderen?
          </p>
          <p className="text-xs text-red-600 mb-3">
            Dit verwijdert de collector, alle kringen en alle foto&apos;s permanent. Dit kan niet ongedaan worden gemaakt.
          </p>
          {deleteError && (
            <div className="text-sm text-red-600 bg-red-100 px-3 py-2 rounded-lg mb-3">{deleteError}</div>
          )}
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleDelete}
              disabled={deleting}
              className="px-4 py-2 text-sm font-semibold bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50"
            >
              {deleting ? 'Verwijderen...' : 'Definitief Verwijderen'}
            </button>
            <button
              type="button"
              onClick={() => { setShowDeleteConfirm(false); setDeleteError(null); }}
              className="px-4 py-2 text-sm font-semibold text-td-muted hover:text-td-navy transition"
            >
              Annuleren
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
